import { setDestroyItems, addDestroy } from '../../../../../../../submodules/web-shared/utils';
import { FIELD_NAMES, LocationFormParams, initialValues, originConfigInitValues } from '../consts';
import { SingleLocationData } from '../../../api/types/SingleLocationData';
import { CreateLocationParams } from '../../../api/types/CreateLocationParams';
import { EditLocationParams } from '../../../api/types/EditLocationParams';
import { LocationAdditionalAttributes } from '../../../api/types/LocationAdditionalAttributes';
import { UpdateLocationAdditionalAttributesParams } from '../../../api/types/UpdateLocationAdditionalAttributesParams';
import { LOCATION_TYPE } from '../../../api/enums/location-types';
import { DefaultAutocompleteOption } from '../../../../../../../submodules/web-shared/api/autocomplete/types/DefaultAutocompleteOption';

/**
 * Converts form values to format required by BE
 */

export const convertFormLocationsValuesForSubmit = (
  values: LocationFormParams,
  initialData?: SingleLocationData | null
): CreateLocationParams | EditLocationParams => {

  const currentCircleArea = values[FIELD_NAMES.CIRCLE_AREA];
  const currentPolygonArea = values[FIELD_NAMES.POLYGON_AREA];

  const hasInitialCircleArea = !!initialData?.[FIELD_NAMES.CIRCLE_AREA].radius;
  const hasInitialPolygonArea = !!initialData?.[FIELD_NAMES.POLYGON_AREA].lng_lat?.length;

  const initialCircleArea = hasInitialCircleArea ? initialData[FIELD_NAMES.CIRCLE_AREA] : null;
  const initialPolygonArea = hasInitialPolygonArea ? initialData[FIELD_NAMES.POLYGON_AREA] : null;

  const dataFields = {
    type: (initialData || values)[FIELD_NAMES.LOCATION_TYPE]!,
    title: values[FIELD_NAMES.LOCATION_NAME]!.trim(),
    custom_title: values[FIELD_NAMES.LOCATION_TITLE],
    address: values[FIELD_NAMES.LOCATION_ADDRESS],
    circle_area: addDestroy(initialCircleArea, currentCircleArea),
    polygon_area: addDestroy(initialPolygonArea, currentPolygonArea),
  };

  const sharedFields = initialData ?
    {
      ...dataFields,
      id: initialData.id,
    }
    : dataFields;

  switch (values[FIELD_NAMES.LOCATION_TYPE]) {
    case LOCATION_TYPE.AIRPORT:
      return {
        ...sharedFields,
        iata: values[FIELD_NAMES.IATA],
        icao: values[FIELD_NAMES.ICAO],
      };
    case LOCATION_TYPE.SEAPORT:
      return {
        ...sharedFields,
        unlocode: values[FIELD_NAMES.UNLOCODE],
        location_schedule: initialData?.location_schedule
          ? addDestroy(initialData.location_schedule, values[FIELD_NAMES.LOCATION_SCHEDULE])
          : values[FIELD_NAMES.LOCATION_SCHEDULE],
      };
    case LOCATION_TYPE.HOT_ZONE:
      return {
        ...sharedFields,
        portal: { id: values[FIELD_NAMES.PORTAL]!.id },
      };
    case LOCATION_TYPE.SAFE_ZONE: {
      const companies = (values[FIELD_NAMES.CARRIER_COMPANIES] || []).map(({ id }) => ({ id }));
      return {
        ...sharedFields,
        portal: { id: values[FIELD_NAMES.PORTAL]!.id },
        tracking_config: {
          ignore_event_types: values[FIELD_NAMES.IGNORE_EVENT_TYPES]
        },
        companies: initialData
          ? setDestroyItems({
            oldItems: initialData.zone_companies || [],
            formItems: companies,
          })
          : companies,
        location_schedule: initialData?.location_schedule
          ? addDestroy(initialData.location_schedule, values[FIELD_NAMES.LOCATION_SCHEDULE])
          : values[FIELD_NAMES.LOCATION_SCHEDULE],
      };
    }
    case LOCATION_TYPE.DANGER_ZONE:
    case LOCATION_TYPE.LOW_COVERAGE_ZONE:
    case LOCATION_TYPE.OPEN_MIC_ZONE: {
      return {
        ...sharedFields,
        portal: values[FIELD_NAMES.GLOBAL] ? null : { id: values[FIELD_NAMES.PORTAL]!.id },
      };
    }
    default: {
      const relayLocations = values[FIELD_NAMES.RELAY_LOCATIONS].map(({ id }) => ({ id }));
      const alternativeDeliveryLocations = values[FIELD_NAMES.ALTERNATE_DELIVERY_LOCATIONS].map(({ id }) => ({ id }));
      const notes = values[FIELD_NAMES.NOTES] || '';

      return {
        ...sharedFields,
        portal: { id: values[FIELD_NAMES.PORTAL]!.id },
        notes: notes.trim(),
        relay_locations: initialData?.relay_locations ?
          setDestroyItems({
            oldItems: initialData[FIELD_NAMES.RELAY_LOCATIONS],
            formItems: relayLocations
          })
          : relayLocations,
        location_schedule: initialData?.location_schedule
          ? addDestroy(initialData.location_schedule, values[FIELD_NAMES.LOCATION_SCHEDULE])
          : values[FIELD_NAMES.LOCATION_SCHEDULE],
        alternate_delivery_locations: initialData?.alternate_delivery_locations ?
          setDestroyItems({
            oldItems: initialData[FIELD_NAMES.ALTERNATE_DELIVERY_LOCATIONS],
            formItems: alternativeDeliveryLocations
          })
          : alternativeDeliveryLocations
      };
    }
  }
};

export const convertFormAdditionalValuesForSubmit = (
  values: LocationFormParams,
  initialData: LocationAdditionalAttributes
): UpdateLocationAdditionalAttributesParams => {

  const extractIds = (items: DefaultAutocompleteOption[]) => items.map(({ id }) => ({ id }));

  const brokerCompanies = extractIds(values[FIELD_NAMES.ORIGIN_CONFIG][FIELD_NAMES.BROKERS]);
  const customerCompanies = extractIds(values[FIELD_NAMES.ORIGIN_CONFIG][FIELD_NAMES.CUSTOMERS]);
  const carrierCompanies = extractIds(values[FIELD_NAMES.ORIGIN_CONFIG][FIELD_NAMES.CARRIERS]);
  const drivers = extractIds(values[FIELD_NAMES.ORIGIN_CONFIG][FIELD_NAMES.DRIVERS]);
  const workflows = extractIds(values[FIELD_NAMES.ORIGIN_CONFIG][FIELD_NAMES.WORKFLOWS]);
  const childOrigins = extractIds(values[FIELD_NAMES.ORIGIN_CONFIG][FIELD_NAMES.CHILD_ORIGINS]);
  const parentOrigins = extractIds(values[FIELD_NAMES.ORIGIN_CONFIG][FIELD_NAMES.PARENT_ORIGINS]);

  return {
    location_id: initialData.location_id,
    portal_id: values[FIELD_NAMES.PORTAL]!.id,
    origin_config: {
      returned_logic:
        values[FIELD_NAMES.ORIGIN_CONFIG][FIELD_NAMES.RETURNED_LOGIC] ?? false,
      broker_required:
        values[FIELD_NAMES.ORIGIN_CONFIG][FIELD_NAMES.BROKER_REQUIRED] ?? false,
      shipment_ref_optional:
        values[FIELD_NAMES.ORIGIN_CONFIG][FIELD_NAMES.SHIPMENT_REF_TOGGlE] ?? false,
      customer_companies: initialData?.origin_config.customer_companies
        ? setDestroyItems({
          oldItems: initialData.origin_config.customer_companies,
          formItems: customerCompanies,
        })
        : customerCompanies,
      broker_companies: initialData?.origin_config.broker_companies
        ? setDestroyItems({
          oldItems: initialData.origin_config.broker_companies,
          formItems: brokerCompanies,
        })
        : brokerCompanies,
      carrier_companies: initialData?.origin_config.carrier_companies
        ? setDestroyItems({
          oldItems: initialData.origin_config.carrier_companies,
          formItems: carrierCompanies,
        })
        : carrierCompanies,
      drivers: initialData?.origin_config.drivers
        ? setDestroyItems({
          oldItems: initialData.origin_config.drivers,
          formItems: drivers,
        })
        : drivers,
      workflow_groups: initialData?.origin_config.workflow_groups
        ? setDestroyItems({
          oldItems: initialData.origin_config.workflow_groups,
          formItems: workflows,
        })
        : workflows,
      child_origins: initialData?.origin_config.child_origins
        ? setDestroyItems({
          oldItems: initialData.origin_config.child_origins,
          formItems: childOrigins,
        })
        : childOrigins,
      parent_origins: initialData?.origin_config.parent_origins
        ? setDestroyItems({
          oldItems: initialData.origin_config.parent_origins,
          formItems: parentOrigins,
        })
        : parentOrigins,
    }
  };
};

export const prepareFormValues = (
  locationData: SingleLocationData,
  locationAttributes: LocationAdditionalAttributes | null
): LocationFormParams => {
  const {
    type,
    portal,
    name,
    title,
    tracking_config,
    notes,
    zone_companies,
    address,
    latitude,
    longitude,
    location_schedule,
    iata,
    icao,
    unlocode,
    city,
    country,
    zip,
    street,
    state
  } = locationData;

  const origin_config = locationAttributes?.origin_config ?? {};

  const polygonArea = !locationData.polygon_area.lng_lat?.length ? null : {
    lng_lat: locationData.polygon_area.lng_lat,
    percent: locationData.polygon_area.percent,
  };

  const circleArea = polygonArea || !locationData.circle_area.radius ? null : {
    radius: locationData.circle_area.radius,
    percent: locationData.circle_area.percent,
  };

  return {
    ...initialValues,
    id: locationData.id,
    [FIELD_NAMES.LOCATION_TYPE]: type,
    [FIELD_NAMES.PORTAL]: portal ? { id: portal.id, label: portal.name } : null,
    [FIELD_NAMES.LOCATION_NAME]: name,
    [FIELD_NAMES.LOCATION_TITLE]: title || '',
    [FIELD_NAMES.NOTES]: notes || '',
    [FIELD_NAMES.CARRIER_COMPANIES]: zone_companies,
    [FIELD_NAMES.LOCATION_ADDRESS]: {
      [FIELD_NAMES.CITY]: city,
      [FIELD_NAMES.COUNTRY]: country,
      [FIELD_NAMES.ZIP]: zip,
      [FIELD_NAMES.STREET]: street,
      [FIELD_NAMES.STATE]: state,
      [FIELD_NAMES.ADDRESS]: address,
      [FIELD_NAMES.LATITUDE]: latitude,
      [FIELD_NAMES.LONGITUDE]: longitude
    },
    [FIELD_NAMES.LOCATION_SCHEDULE]: location_schedule,
    [FIELD_NAMES.IATA]: iata,
    [FIELD_NAMES.ICAO]: icao,
    [FIELD_NAMES.UNLOCODE]: unlocode,
    [FIELD_NAMES.RELAY_LOCATIONS]: locationData[FIELD_NAMES.RELAY_LOCATIONS],
    [FIELD_NAMES.ORIGIN_CONFIG]: {
      ...originConfigInitValues,
      ...origin_config
    },
    [FIELD_NAMES.GLOBAL]: !portal,
    [FIELD_NAMES.IGNORE_EVENTS]: tracking_config && tracking_config.ignore_event_types!.length > 0,
    [FIELD_NAMES.IGNORE_EVENT_TYPES]: tracking_config ? tracking_config.ignore_event_types : [],
    [FIELD_NAMES.POLYGON_AREA]: polygonArea,
    [FIELD_NAMES.CIRCLE_AREA]: circleArea,
    [FIELD_NAMES.ALTERNATE_DELIVERY_LOCATIONS]: locationData[FIELD_NAMES.ALTERNATE_DELIVERY_LOCATIONS],
  };
};